/**
 * DependencyGraphView — Make-style build DAG of all 56 deliverables.
 *
 * Rendered with ReactFlow + dagre auto-layout. Nodes coloured by status:
 *   OK            (green)  — generated, in sync
 *   STALE         (amber)  — upstream changed, needs regeneration
 *   EDITOR_EDITED (purple) — last revision came from an editor harvest
 *   MISSING       (gray)   — not yet generated
 *
 * Click a node to focus on it — backend returns just its upstream +
 * downstream cone, which makes the impact of an edit obvious.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import ReactFlow, {
  Background, Controls, MiniMap, MarkerType,
} from "reactflow";
import "reactflow/dist/style.css";
import dagre from "dagre";
import api from "../lib/api";
import { X, RefreshCcw, Filter } from "lucide-react";

const STATUS_STYLE = {
  OK:            { bg: "#0B3B2E", border: "#10B981", fg: "#6EE7B7" },
  STALE:         { bg: "#3F2A0F", border: "#F59E0B", fg: "#FCD34D" },
  EDITOR_EDITED: { bg: "#3F1D3F", border: "#A855F7", fg: "#D8B4FE" },
  MISSING:       { bg: "#1F2937", border: "#6B7280", fg: "#9CA3AF" },
};

const STAGE_COLOR = {
  A: "#60A5FA", B: "#F26B1F", C: "#F472B6", D: "#34D399",
  E: "#FBBF24", F: "#A78BFA", G: "#FB7185", H: "#EF4444",
  I: "#22D3EE", J: "#94A3B8",
};

function layoutGraph(nodes, edges, focusCode) {
  // dagre auto-layout — left-to-right, ranks group by stage chain
  const g = new dagre.graphlib.Graph();
  g.setGraph({
    rankdir: "LR",
    nodesep: 24,
    ranksep: 90,
    marginx: 20,
    marginy: 20,
  });
  g.setDefaultEdgeLabel(() => ({}));

  nodes.forEach((n) => g.setNode(n.id, { width: 170, height: 56 }));
  edges.forEach((e) => g.setEdge(e.source, e.target));

  dagre.layout(g);

  return nodes.map((n) => {
    const pos = g.node(n.id);
    const style = STATUS_STYLE[n.status] || STATUS_STYLE.MISSING;
    const stageColor = STAGE_COLOR[n.stage] || "#9CA3AF";
    const isFocus = n.code === focusCode;

    return {
      id: n.id,
      data: {
        label: (
          <div style={{ display: "flex", flexDirection: "column",
                         gap: 2, lineHeight: 1.2 }}>
            <div style={{ fontFamily: "ui-monospace", fontSize: 10,
                            fontWeight: 700, color: stageColor,
                            letterSpacing: 0.4 }}>
              {n.code}
            </div>
            <div style={{ fontSize: 10, color: style.fg,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap" }}
                  title={n.name}>
              {n.name || "—"}
            </div>
          </div>
        ),
        title: n.title,
        code: n.code,
        status: n.status,
      },
      position: { x: pos.x - 85, y: pos.y - 28 },
      style: {
        background: style.bg,
        border: `${isFocus ? 3 : 1.5}px solid ${isFocus ? "#FFB000" : style.border}`,
        borderRadius: 8,
        padding: "6px 10px",
        width: 170,
        height: 56,
        cursor: "pointer",
        boxShadow: isFocus
          ? "0 0 12px rgba(255, 176, 0, 0.4)"
          : "0 1px 3px rgba(0, 0, 0, 0.4)",
      },
      sourcePosition: "right",
      targetPosition: "left",
    };
  });
}

function styleEdges(edges, focusCode) {
  return edges.map((e) => {
    const onPath = focusCode &&
      (e.source === focusCode || e.target === focusCode);
    return {
      id: e.id,
      source: e.source,
      target: e.target,
      type: "smoothstep",
      animated: onPath,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: onPath ? "#FFB000" : "#475569",
        width: 14, height: 14,
      },
      style: {
        stroke: onPath ? "#FFB000" : "#475569",
        strokeWidth: onPath ? 2 : 1,
      },
    };
  });
}

export default function DependencyGraphView({ projectId, onClose }) {
  const [graph, setGraph] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [focusCode, setFocusCode] = useState(null);

  const load = useCallback(async (focus = null) => {
    setBusy(true);
    setErr("");
    try {
      const url = focus
        ? `/projects/${projectId}/sync/dependency-graph?focus=${focus}`
        : `/projects/${projectId}/sync/dependency-graph`;
      const r = await api.get(url);
      setGraph(r.data);
      setFocusCode(focus);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const { rfNodes, rfEdges } = useMemo(() => {
    if (!graph) return { rfNodes: [], rfEdges: [] };
    return {
      rfNodes: layoutGraph(graph.nodes, graph.edges, focusCode),
      rfEdges: styleEdges(graph.edges, focusCode),
    };
  }, [graph, focusCode]);

  const onNodeClick = (_evt, node) => {
    const code = node?.data?.code;
    if (!code) return;
    if (focusCode === code) load(null);  // toggle off
    else load(code);
  };

  const summary = graph?.summary || {};

  return (
    <div data-testid="dependency-graph-modal"
         style={{
           position: "fixed", inset: 0, zIndex: 100,
           background: "rgba(7, 11, 22, 0.92)",
           display: "flex", flexDirection: "column",
         }}>
      {/* Toolbar */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "12px 18px",
        background: "rgba(15, 23, 42, 0.95)",
        borderBottom: "1px solid #1F2937",
      }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: "#F3F4F6" }}>
          Deliverable Dependency Graph
        </div>
        <span style={{ color: "#6B7280", fontSize: 12 }}>·</span>
        {focusCode && (
          <span data-testid="dep-graph-focus-pill"
                style={{
                  background: "#3F2A0F", border: "1px solid #FFB000",
                  color: "#FCD34D", padding: "3px 10px",
                  borderRadius: 999, fontSize: 11, fontWeight: 600,
                  display: "inline-flex", alignItems: "center", gap: 6,
                }}>
            <Filter size={11} /> Focus: {focusCode}
            <button onClick={() => load(null)}
                    style={{ background: "transparent", border: "none",
                              color: "#FCD34D", cursor: "pointer",
                              padding: 0, marginLeft: 2 }}>
              ×
            </button>
          </span>
        )}
        {!focusCode && (
          <span style={{ fontSize: 11, color: "#9CA3AF" }}>
            Click any node to see its upstream + downstream cone
          </span>
        )}

        <div style={{ flex: 1 }} />

        {/* Legend / counts */}
        <div style={{ display: "flex", gap: 10, fontSize: 11 }}>
          {[
            ["OK", summary.ok, "OK"],
            ["Stale", summary.stale, "STALE"],
            ["Editor", summary.editor_edited, "EDITOR_EDITED"],
            ["Missing", summary.missing, "MISSING"],
          ].map(([label, count, key]) => (
            <div key={key} style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              padding: "3px 9px", borderRadius: 999,
              background: STATUS_STYLE[key].bg,
              border: `1px solid ${STATUS_STYLE[key].border}`,
              color: STATUS_STYLE[key].fg,
              fontFamily: "ui-monospace",
            }}>
              <span style={{
                width: 6, height: 6, borderRadius: "50%",
                background: STATUS_STYLE[key].border,
              }} />
              <span style={{ fontWeight: 700 }}>{count ?? 0}</span>
              <span>{label}</span>
            </div>
          ))}
        </div>

        <button data-testid="dep-graph-refresh-btn"
                onClick={() => load(focusCode)} disabled={busy}
                title="Refresh"
                style={{
                  background: "transparent", border: "1px solid #374151",
                  color: "#D1D5DB", padding: "5px 10px", borderRadius: 6,
                  fontSize: 11, cursor: busy ? "wait" : "pointer",
                  display: "inline-flex", alignItems: "center", gap: 5,
                }}>
          <RefreshCcw size={11} className={busy ? "spin" : ""} />
          Refresh
        </button>

        <button data-testid="dep-graph-close-btn"
                onClick={onClose}
                style={{
                  background: "#374151", color: "#F3F4F6",
                  border: "none", padding: "6px 10px", borderRadius: 6,
                  fontSize: 12, cursor: "pointer",
                  display: "inline-flex", alignItems: "center", gap: 5,
                }}>
          <X size={13} /> Close
        </button>
      </div>

      {/* Canvas */}
      <div style={{ flex: 1, position: "relative" }}>
        {err && (
          <div style={{
            position: "absolute", top: 12, left: 12, zIndex: 10,
            background: "#3F0F0F", border: "1px solid #EF4444",
            color: "#FCA5A5", padding: "8px 12px", borderRadius: 6,
            fontSize: 12,
          }}>
            Failed to load graph: {err}
          </div>
        )}

        {graph && (
          <ReactFlow
            nodes={rfNodes}
            edges={rfEdges}
            onNodeClick={onNodeClick}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            proOptions={{ hideAttribution: true }}
            minZoom={0.2}
            maxZoom={2}
          >
            <Background color="#1F2937" gap={24} size={1} />
            <Controls position="bottom-right"
                        style={{ background: "#0B1220",
                                  border: "1px solid #374151" }} />
            <MiniMap
              position="bottom-left"
              nodeColor={(n) => {
                const status = n?.data?.status || "MISSING";
                return STATUS_STYLE[status].border;
              }}
              maskColor="rgba(7,11,22,0.7)"
              style={{ background: "#0B1220",
                        border: "1px solid #374151" }}
            />
          </ReactFlow>
        )}
      </div>
    </div>
  );
}
